import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SidebarItem } from "@/types/navigation";

const WIDTH = 256;

export function NavSidebar({
  open,
  onClose,
  title,
  items,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  items: SidebarItem[];
}) {
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 sm:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "h-full overflow-hidden bg-surface border-r border-border flex flex-col shrink-0",
          "transition-[width] duration-260 ease-[cubic-bezier(0.2,0,0,1)]",
          "sm:relative sm:z-auto max-sm:fixed max-sm:left-0 max-sm:top-0 max-sm:z-40 max-sm:h-full",
        )}
        style={{ width: open ? WIDTH : 0 }}
        aria-label={title}
        aria-hidden={!open}
      >
        <div className="flex flex-col h-full" style={{ width: WIDTH }}>
          <div className="flex items-center justify-between h-10 px-4 border-b border-border shrink-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted truncate">
              {title}
            </span>
            <button
              onClick={onClose}
              className="p-1 rounded-md text-muted hover:text-primary hover:bg-surface-elevated transition-colors cursor-pointer shrink-0"
              aria-label="Close navigation"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-2 px-2">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.href}
                  end
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors mb-0.5",
                      isActive
                        ? "bg-brand-cyan/10 text-brand-cyan font-medium"
                        : "text-secondary hover:bg-surface-elevated hover:text-primary",
                    )
                  }
                >
                  {Icon && <Icon className="h-4 w-4 shrink-0 opacity-70" />}
                  <span className="flex-1 min-w-0 truncate">{item.label}</span>
                  {item.badge != null && (
                    <span className="text-xs text-muted font-mono bg-surface-elevated px-1.5 py-0.5 rounded-full shrink-0">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}
